import { lazy, Suspense } from "react";
import { RouteObject, useParams } from "../../helpers/tixxt-router";
import { convertRoutes } from "../tixxt/BackboneFeature";
import Loading from "../shared/Loading";

const NetworkSettings = lazy(() => import("./networkSettings"));
const NavigationManager = lazy(() => import("./NavigationManager"));
const GroupCategorySorter = lazy(() => import("./GroupCategorySorter"));
const GroupCategoryEdit = lazy(() => import("./groupCategories"));
const MembershipEdit = lazy(() => import("./MembershipEdit"));
const MembershipNew = lazy(() => import("./MembershipNew"));
const LegalTextEdit = lazy(
  () => import("../administration/legalTexts/LegalTextEdit"),
);
const LegalTextIndex = lazy(
  () => import("../administration/legalTexts/LegalTextIndex"),
);
const LegalTextNew = lazy(
  () => import("../administration/legalTexts/LegalTextNew"),
);
const LegalTextVersions = lazy(
  () => import("../administration/legalTexts/LegalTextVersions"),
);
const ImprintEdit = lazy(
  () => import("../administration/imprints/ImprintEdit"),
);
const AppNavigationManager = lazy(() => import("./appNavigationManager"));
const GroupAdministrationMainPage = lazy(
  () => import("../../components/administration/GroupAdministrationMainPage"),
);
const NewInvitePage = lazy(
  () => import("../../components/administration/invites/NewInvitePage"),
);
const DesignsOverviewPage = lazy(
  () => import("./designManager/DesignsOverviewPage"),
);
const DesignsOverviewLightPage = lazy(
  () => import("./designManagerLight/DesignsOverviewLightPage"),
);
const DesignManagerPage = lazy(
  () => import("./designManager/DesignManagerPage"),
);
const DesignManagerLightPage = lazy(
  () => import("./designManagerLight/DesignManagerLightPage"),
);
const StatisticsPage = lazy(() => import("./statistics/StatisticsPage"));
const TagsManagerPage = lazy(() => import("./tags/TagsManagerPage"));
const NewAppPage = lazy(() => import("./appCreator/NewAppPage"));
const EditAppPage = lazy(() => import("./appCreator/EditAppPage"));
const MembershipRolesManagerPage = lazy(
  () => import("./MembershipRolesManagerPage"),
);
const EditWidgetbarPage = lazy(() => import("./widgetbar/EditWidgetbarPage"));
const IndexWidgetbarPage = lazy(() => import("./widgetbar/IndexWidgetbarPage"));
const OrganisationsIndexPage = lazy(
  () => import("./organisations/OrganisationsIndexPage"),
);
const OrganisationDetailsPage = lazy(
  () => import("./organisations/OrganisationDetailsPage"),
);
const OrganisationNewPage = lazy(
  () => import("./organisations/OrganisationNewPage"),
);
const OrganisationEditPage = lazy(
  () => import("./organisations/OrganisationEditPage"),
);
const OrganisationsUnassigned = lazy(
  () => import("./organisations/OrganisationsUnassigned"),
);
const OrganisationSettingsPage = lazy(
  () => import("./organisations/OrganisationSettingsPage"),
);
const EditMembershipChannelSubscriptionsPage = lazy(
  () => import("./EditMembershipChannelSubscriptionsPage"),
);
const EditMembershipOrganisationAssignments = lazy(
  () => import("./EditMembershipOrganisationAssignments"),
);

function Params({ children }) {
  const params = useParams();
  return children(params);
}

const backboneRoutes: RouteObject[] = convertRoutes({
  feature: "Administration",
  featureRoutes: {
    "group-categories/:group_category_id/administration/groups":
      "editSectionGroups",
    "administration/groups": "editGroups",
    "(groups/:group_slug/)administration/members": "editMembers",
    "administration/members/:id/group-memberships":
      "editMemberGroupMemberships",
    "(groups/:group_slug/)administration/consumers": "consumers",
    "administration/consumers/new": "newConsumer",
    "administration/consumers/:id/edit": "editConsumer",
    "administration/consumers/:id/group-memberships":
      "editConsumerGroupMemberships",
    "administration/abbreviations": "abbreviations",
    "administration/abbreviations/new": "newAbbreviation",
    "administration/abbreviations/:id/edit": "editAbbreviation",
    "groups/:group_slug/administration/organisation": "organisationMain",
    "groups/:group_slug/administration/organisation/group-memberships":
      "organisationGroups",
    "groups/:group_slug/administration/organisation/group-memberships/:id":
      "organisationGroupMemberships",
    "groups/:group_slug/administration/organisation/members":
      "organisationMembers",
    "groups/:group_slug/administration/organisation/members/new":
      "newOrganisationMember",
    "groups/:group_slug/administration/organisation/edit":
      "organisationProfile",
    "groups/:group_slug/administration/organisation/feedback":
      "organisationFeedback",
    "administration/industrials": "industrials",
    "administration/industrials/new": "newIndustrial",
    "administration/industrials/:id/edit": "editIndustrial",
  },
});

const reactRoutes: RouteObject[] = [
  {
    path: "/administration/settings/edit",
    element: (
      <Suspense fallback={<Loading />}>
        <NetworkSettings />
      </Suspense>
    ),
  },
  {
    path: "/administration/navigation/edit",
    element: (
      <Suspense fallback={<Loading />}>
        <NavigationManager groupSlug={null} />
      </Suspense>
    ),
  },
  {
    path: "/administration/app_navigation/edit",
    element: (
      <Suspense fallback={<Loading />}>
        <AppNavigationManager />
      </Suspense>
    ),
  },
  {
    path: "/administration/group-categories-order/edit",
    element: (
      <Suspense fallback={<Loading />}>
        <GroupCategorySorter />
      </Suspense>
    ),
  },
  {
    path: "/administration/group-categories/new",
    element: (
      <Suspense fallback={<Loading />}>
        <GroupCategoryEdit />
      </Suspense>
    ),
  },
  {
    path: "/administration/group-categories/:categoryId/edit",
    element: (
      <Suspense fallback={<Loading />}>
        <GroupCategoryEdit />
      </Suspense>
    ),
  },
  {
    path: "/administration/members/:membershipId/edit",
    element: (
      <Suspense fallback={<Loading />}>
        <MembershipEdit />
      </Suspense>
    ),
  },
  {
    path: "/administration/members/new",
    element: (
      <Suspense fallback={<Loading />}>
        <MembershipNew />
      </Suspense>
    ),
  },
  {
    path: "/administration/invites/new",
    element: (
      <Suspense fallback={<Loading />}>
        <NewInvitePage />
      </Suspense>
    ),
  },
  {
    path: "/administration/legal_texts",
    element: (
      <Suspense fallback={<Loading />}>
        <LegalTextIndex />
      </Suspense>
    ),
  },
  {
    path: "/administration/legal_texts/:legalTextId/edit",
    element: (
      <Suspense fallback={<Loading />}>
        <LegalTextEdit />
      </Suspense>
    ),
  },
  {
    path: "/administration/legal_texts/new",
    element: (
      <Suspense fallback={<Loading />}>
        <LegalTextNew />
      </Suspense>
    ),
  },
  {
    path: "/administration/legal_texts/:legalTextId",
    element: (
      <Suspense fallback={<Loading />}>
        <LegalTextVersions />
      </Suspense>
    ),
  },
  {
    path: "/administration/organisations",
    element: (
      <Suspense fallback={<Loading />}>
        <OrganisationsIndexPage />
      </Suspense>
    ),
  },
  {
    path: "/administration/organisations/new",
    element: (
      <Suspense fallback={<Loading />}>
        <OrganisationNewPage />
      </Suspense>
    ),
  },
  {
    path: "/administration/organisations/unassigned",
    element: (
      <Suspense fallback={<Loading />}>
        <OrganisationsUnassigned />
      </Suspense>
    ),
  },
  {
    path: "/administration/organisations/:organisationId",
    element: (
      <Suspense fallback={<Loading />}>
        <OrganisationDetailsPage />
      </Suspense>
    ),
  },
  {
    path: "/administration/organisations/:organisationId/edit",
    element: (
      <Suspense fallback={<Loading />}>
        <OrganisationEditPage />
      </Suspense>
    ),
  },
  {
    path: "/administration/organisation_settings/edit",
    element: (
      <Suspense fallback={<Loading />}>
        <OrganisationSettingsPage />
      </Suspense>
    ),
  },
  {
    path: "/administration/imprint/edit",
    element: (
      <Suspense fallback={<Loading />}>
        <ImprintEdit />
      </Suspense>
    ),
  },
  {
    path: "/administration/statistics",
    element: (
      <Suspense fallback={<Loading />}>
        <StatisticsPage />
      </Suspense>
    ),
    forceTurbo: true, // for user tracking #3324
  },
  {
    path: "/administration/designs",
    element: (
      <Suspense fallback={<Loading />}>
        <DesignsOverviewPage themeType={"network"} />
      </Suspense>
    ),
  },
  {
    path: "/administration/designs/:id/edit",
    element: (
      <Suspense fallback={<Loading />}>
        <DesignManagerPage themeType={"network"} />
      </Suspense>
    ),
  },
  {
    path: "/administration/apps/new",
    element: (
      <Suspense fallback={<Loading />}>
        <NewAppPage />
      </Suspense>
    ),
  },
  {
    path: "/administration/apps/:appSlug/edit",
    element: (
      <Suspense fallback={<Loading />}>
        <EditAppPage />
      </Suspense>
    ),
  },
  {
    path: "/group-categories/:groupCategoryId/administration/designs",
    element: (
      <Suspense fallback={<Loading />}>
        <DesignsOverviewLightPage themeType={"groupCategory"} />
      </Suspense>
    ),
  },
  {
    path: "/group-categories/:groupCategoryId/administration/designs/:designId/edit",
    element: (
      <Suspense fallback={<Loading />}>
        <DesignManagerLightPage themeType={"groupCategory"} />
      </Suspense>
    ),
  },
  {
    path: "/group-categories/:groupCategoryId/administration/invites/new",
    element: (
      <Suspense fallback={<Loading />}>
        <NewInvitePage />
      </Suspense>
    ),
  },
  {
    path: "/administration/tags",
    element: (
      <Suspense fallback={<Loading />}>
        <TagsManagerPage />
      </Suspense>
    ),
  },
  {
    path: "/groups/:groupSlug/administration",
    element: (
      <Suspense fallback={<Loading />}>
        <GroupAdministrationMainPage />
      </Suspense>
    ),
  },
  {
    path: "/groups/:groupSlug/administration/invites/new",
    element: (
      <Suspense fallback={<Loading />}>
        <NewInvitePage />
      </Suspense>
    ),
  },
  {
    path: "/groups/:groupSlug/membership_invites/new",
    element: (
      <Suspense fallback={<Loading />}>
        <NewInvitePage />
      </Suspense>
    ),
  },
  {
    path: "/groups/:groupSlug/administration/designs",
    element: (
      <Suspense fallback={<Loading />}>
        <DesignsOverviewLightPage themeType={"group"} />
      </Suspense>
    ),
  },
  {
    path: "/groups/:groupSlug/administration/designs/:designId/edit",
    element: (
      <Suspense fallback={<Loading />}>
        <DesignManagerLightPage themeType={"group"} />
      </Suspense>
    ),
  },
  {
    path: "/groups/:groupSlug/administration/navigation/edit",
    element: (
      <Suspense fallback={<Loading />}>
        <Params>
          {({ groupSlug }) => <NavigationManager groupSlug={groupSlug} />}
        </Params>
      </Suspense>
    ),
  },
  {
    path: "/groups/:groupSlug/administration/statistics",
    element: (
      <Suspense fallback={<Loading />}>
        <StatisticsPage />
      </Suspense>
    ),
  },
  {
    path: "/groups/:groupSlug/administration/apps/new",
    element: (
      <Suspense fallback={<Loading />}>
        <NewAppPage />
      </Suspense>
    ),
  },
  {
    path: "/groups/:groupSlug/administration/apps/:appSlug/edit",
    element: (
      <Suspense fallback={<Loading />}>
        <EditAppPage />
      </Suspense>
    ),
  },
  {
    path: "/administration/membership_roles",
    element: (
      <Suspense fallback={<Loading />}>
        <MembershipRolesManagerPage groupSlug={null} />
      </Suspense>
    ),
  },
  {
    path: "/groups/:groupSlug/administration/membership_roles",
    element: (
      <Suspense fallback={<Loading />}>
        <Params>
          {({ groupSlug }) => (
            <MembershipRolesManagerPage groupSlug={groupSlug} />
          )}
        </Params>
      </Suspense>
    ),
  },
  {
    path: "/administration/widgetbars",
    element: (
      <Suspense fallback={<Loading />}>
        <IndexWidgetbarPage />
      </Suspense>
    ),
  },
  {
    path: "/administration/members/:membershipId/channel-subscriptions",
    element: (
      <Suspense fallback={<Loading />}>
        <EditMembershipChannelSubscriptionsPage />
      </Suspense>
    ),
  },
  {
    path: "/administration/organisations/assignments/:membershipId",
    element: (
      <Suspense fallback={<Loading />}>
        <EditMembershipOrganisationAssignments />
      </Suspense>
    ),
  },
  {
    path: "/groups/:groupSlug/administration/widgetbars",
    element: (
      <Suspense fallback={<Loading />}>
        <Params>
          {({ groupSlug }) => <IndexWidgetbarPage groupId={groupSlug} />}
        </Params>
      </Suspense>
    ),
  },
  {
    path: "/administration/widgetbars/:identifier/edit",
    element: (
      <Suspense fallback={<Loading />}>
        <Params>
          {({ identifier }) => <EditWidgetbarPage identifier={identifier} />}
        </Params>
      </Suspense>
    ),
  },
  {
    path: "/groups/:groupSlug/administration/widgetbars/:identifier/edit",
    element: (
      <Suspense fallback={<Loading />}>
        <Params>
          {({ identifier, groupSlug }) => (
            <EditWidgetbarPage identifier={identifier} groupId={groupSlug} />
          )}
        </Params>
      </Suspense>
    ),
  },
];
export default backboneRoutes.concat(reactRoutes);
